import * as Yup from "yup";
import { TStep } from "../types";
import { EligibleIntro, EligibleReview } from "../steps/eligible";
import { applyingOnBehalfOf, methodOfContact, partyType, tellUsMoreOptions, yesNo, yesNoUnsure } from "./questionOptions";
import { InformationalPopover } from "../../InformationalPopover/InformationalPopover";

export const eligibleSteps: {
  [key: string]: TStep;
} = {
  "eligible-step-one": {
    title: "We can help you with free telephone legal advice",
    note: `We’ll need a few more details from you to complete your application. This should only take a couple
    of minutes.`,
    type: "form-content",
    component: <EligibleIntro />,
    next: "eligible-step-two",
    stepNumber: "12",
    customButtonText: "Continue your application",
  },
  "eligible-step-two": {
    title: "Describe your concern in more detail",
    type: "form",
    note: (
      <>
        <p style={{maxWidth: 700}}>
          We need some information to help us consider your request and respond as quickly as possible. Please include the following information if relevant:
        </p>
        <p style={{maxWidth: 700}}>
          <ul>
            <li>Clearly specify what your legal question is and what assistance you are seeking from EDO</li>
            <li>What is happening? Describe the project/proposal and include the Project Title or Identifier if known</li>
            <li>Where is it? Include as many details of the location as possible including the address or local government area or name of the National Park/reserve</li>
            <li>What is the impact? Describe the potential impact on the environment or community for example is there any risk to cultural heritage or ecologically endangered or threatened species likely to be impacted?</li>
            <li>What stage is it at? Has a decision been made, are works underway or is it still under assessment / consideration?</li>
          </ul>
        </p>
        <InformationalPopover description="New education campus titled New Education Campus Lake Beauty (SSD-123459). Approval given and work begun. Conditions of Consent requiring daily surveys, staged clearing and installation of nesting boxes are being breached. I seek advice as to what I can do to ensure that the conditions are met." />
      </>
    ),
    components: [
      {
        type: "textarea",
        name: "concernDescription",
        validation: Yup.string()
          .max(1000, "Over character limit")
          .required("Please write some details about your concern"),
      },
    ],
    next: "eligible-step-three",
    previous: "eligible-step-one",
    submit: false,
    stepNumber: "13",
    stepPath: "/apply/describe",
    stepText: "Step 1 of 5",
  },
  "eligible-step-three": {
    title: "Is this matter time-sensitive, or is there a deadline?",
    note: "For example: a court or tribunal date or a submission deadline.",
    type: "form",
    components: [
      {
        type: "radio",
        name: "timeSensitive",
        options: yesNoUnsure,
        validation: Yup.string().required("Please select at least 1"),
      },
      {
        label: "When is the deadline?",
        type: "date",
        name: "timeSensitiveDate",
        condition: { timeSensitive: "yes" },
        validation: Yup.string()
          .nullable()
          .when("timeSensitive", { is: "yes", then: Yup.string().required() }),
      },
    ],
    next: "eligible-step-four",
    previous: "eligible-step-two",
    submit: false,
    stepNumber: "14",
    stepPath: "/apply/contact-details",
    stepText: "Step 2 of 5",
  },
  "eligible-step-four": {
    title: "Is there another party involved?",
    note: `Please tell us the name of the person, company or entity proposing/undertaking the action causing your concern.
    We need this information so that we can check if we have a conflict of interest in providing any legal advice to you.
    `,
    type: "form",
    components: [
      {
        type: "radio",
        name: "otherPartyInvolved",
        options: yesNoUnsure,
        validation: Yup.string().required("Please select at least 1"),
      },
      {
        label: "Who is the other party?",
        type: "select",
        name: "otherPartyInvolvedType",
        options: partyType,
        condition: { otherPartyInvolved: "yes" },
        validation: Yup.string().when("otherPartyInvolved", {
          is: "yes",
          then: Yup.string().required(),
        }),
      },
      {
        label: "Full name/name of company, council, department*",
        type: "text",
        name: "otherPartyName",
        condition: { otherPartyInvolved: "yes" },
        validation: Yup.string().when("otherPartyInvolved", {
          is: "yes",
          then: Yup.string().required(),
        }),
      },
      {
        label: "Street address (if known)",
        type: "address",
        name: "otherPartyAddress",
        condition: { otherPartyInvolved: "yes" },
      },
    ],
    next: "eligible-step-five",
    previous: "eligible-step-three",
    submit: false,
    stepNumber: "15",
    stepText: "Step 3 of 5",
  },
  "eligible-step-five": {
    title: "Are you applying on behalf of a group or incorporated association?",
    type: "form",
    components: [
      {
        type: "radio",
        name: "applyingOnBehalf",
        options: yesNo,
        validation: Yup.string().required("Please select at least 1"),
      },
      {
        label: "What kind of group or incorporated association?",
        type: "select",
        name: "applyingOnBehalfGroupType",
        options: applyingOnBehalfOf,
        condition: { applyingOnBehalf: "yes" },
        validation: Yup.string().when("applyingOnBehalf", {
          is: "yes",
          then: Yup.string().required("This information is required"),
        }),
      },
      {
        label: "Name of community group/incorporated association",
        type: "text",
        name: "applyingOnBehalfName",
        condition: { applyingOnBehalf: "yes" },
        validation: Yup.string().when("applyingOnBehalf", {
          is: "yes",
          then: Yup.string().required("This information is required"),
        }),
      },
      {
        label: "Main purpose (This may be found in your goals and objectives document)",
        type: "text",
        name: "applyingOnBehalfPurpose",
        condition: { applyingOnBehalf: "yes" },
      },
    ],
    next: (values) => {
      if (values.applyingOnBehalf === "yes") {
        return "eligible-step-seven-two";
      } else return "eligible-step-six";
    },
    previous: "eligible-step-four",
    submit: false,
    stepNumber: "16",
    stepText: "Step 4 of 5",
  },
  "eligible-step-six": {
    title: "Tell us a bit about you",
    note: "Select any of the following that apply to you. This information is optional. You do not have to answer these questions. Any answer you give will help us assess your application. Some of our services are provided with the support of government funding, this information will help us report on the services we provide.",
    type: "form",
    components: [
      {
        type: "checkbox",
        name: "tellUsMore",
        options: tellUsMoreOptions,
        label: "Do any of the following apply to you?",
        note: "Please select all that apply",
      },
    ],
    next: "eligible-step-seven",
    previous: "eligible-step-five",
    submit: false,
    stepNumber: "21",
    stepText: "Step 5 of 5",
  },
  "eligible-step-seven": {
    title: "Please provide your details",
    note: `We will use this information to contact you and to make sure we don’t have a conflict of interest,
    so we can help you with your concern.`,
    type: "form",
    components: [
      {
        type: "text",
        name: "contactFirstName",
        label: "Given name*",
        validation: Yup.string().required("Please enter valid first name"),
      },
      {
        type: "text",
        name: "contactLastName",
        label: "Family name*",
        validation: Yup.string().required("Please enter valid last name"),
      },
      {
        type: "email",
        name: "contactEmail",
        label: "Email*",
        validation: Yup.string().required("Please enter valid email").email("Please enter valid email"),
      },
      {
        type: "date",
        name: "contactBirthDate",
        label: "Date of birth*",
        validation: Yup.date().required("Please enter date of birth"),
      },
      {
        type: "tel",
        name: "contactNumber",
        label: "Contact Number*",
        validation: Yup.string()
          .required("Please enter valid phone number, including area code")
          .matches(
            /^\(?([0-9]{3})\)?([0-9]{3})([0-9]{4})$/,
            "Please enter valid phone number, including area code"
          ),
      },
      {
        type: "address",
        name: "contactAddress",
        label: "Street Address",
      },
      {
        label: "Preferred method of contact",
        type: "select",
        options: methodOfContact,
        name: "preferredContactMethod",
      },
    ],
    next: "eligible-step-eight",
    previous: "eligible-step-six",
    submit: false,
    stepNumber: "18",
    stepText: "Step 5 of 5",
  },
  "eligible-step-seven-two": {
    title: "Provide details about the main contact for this application",
    note: `We will use this information to contact you and to make sure we don’t have a conflict of interest,
    so we can help you with your concern.`,
    type: "form",
    components: [
      {
        type: "text",
        name: "contactFirstName",
        label: "Given name*",
        validation: Yup.string().required("Please enter valid first name"),
      },
      {
        type: "text",
        name: "contactLastName",
        label: "Family name*",
        validation: Yup.string().required("Please enter valid last name"),
      },
      {
        type: "email",
        name: "contactEmail",
        label: "Email*",
        validation: Yup.string().required("Please enter valid email").email("Please enter valid email"),
      },
      {
        type: "date",
        name: "contactBirthDate",
        label: "Date of birth*",
        validation: Yup.date().required("Please enter date of birth"),
      },
      {
        type: "tel",
        name: "contactNumber",
        label: "Contact Number*",
        validation: Yup.string()
          .required("Please enter valid phone number, including area code")
          .matches(
            /^\(?([0-9]{3})\)?([0-9]{3})([0-9]{4})$/,
            "Please enter valid phone number, including area code"
          ),
      },
      {
        type: "address",
        name: "contactAddress",
        label: "Street Address",
      },
      {
        label: "Preferred method of contact",
        type: "select",
        options: methodOfContact,
        name: "preferredContactMethod",
      },
    ],
    next: "eligible-step-eight",
    previous: "eligible-step-six",
    submit: false,
    stepNumber: "16.1",
    stepText: "Step 5 of 5",
  },
  "eligible-step-eight": {
    type: "form-content",
    title: "Review your Application",
    note: "Check that the details you’ve provided are correct, then submit your application. <br/><br/> We want you to know that we will treat your information confidentially in accordance with our legal obligations. By submitting your application, you have requested legal advice. You have not entered into a client agreement with EDO.",
    component: <EligibleReview />,
    next: "/apply/eligible-thanks",
    previous: "eligible-step-seven",
    submit: true,
    stepNumber: "19",
    stepPath: "/apply/review",
  },
};
